import React, { useState } from 'react';
import { DatePicker, Input, Popover, Select } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import type { SelectProps } from 'antd';
import dayjs from 'dayjs';
import { useController, useFormContext } from 'react-hook-form';
import type { Path } from 'react-hook-form';
import clsx from 'clsx';
import ConditionalRender from '../../../../shared/components/ConditionalRender';
import type { UserFormValues } from '../../../../types/UserManagementTypes';
import { useUserFormMeta } from './userFormMeta';
import styles from './UserFormModal.module.scss';

// Local fields rather than the shared InputField/SelectField: this wizard needs
// its own dark styling, and antd's error status would otherwise repaint the
// controls white.

interface FieldShellProps {
  label: string;
  required?: boolean;
  error?: string;
  errorClassName?: string;
  children: React.ReactNode;
}

const FieldShell = ({ label, required, error, errorClassName, children }: FieldShellProps) => (
  <div className={styles.field}>
    <label className={styles.fieldLabel}>
      {label}
      <ConditionalRender visible={required} hideFallback>
        <span className={styles.requiredMark}>*</span>
      </ConditionalRender>
    </label>
    {children}
    {/* Always rendered so an appearing error can't shift the layout. */}
    <span className={clsx(styles.errorSlot, errorClassName)}>{error}</span>
  </div>
);

export const InfoIcon = ({ content }: { content: React.ReactNode }) => (
  <Popover content={content} arrow={false} placement="right" rootClassName={styles.infoOverlay}>
    <img src="/assets/info-icon.svg" alt="" className={styles.infoIcon} />
  </Popover>
);

interface TextInputFieldProps {
  name: Path<UserFormValues>;
  label: string;
  placeholder: string;
  required?: boolean;
  maxLength?: number;
  /** Drops anything that isn't a digit as the user types or pastes. */
  digitsOnly?: boolean;
  /** Fires on blur with the field's current value; used by the email-change warning. */
  onFieldBlur?: (value: string) => void;
}

export const TextInputField = ({ name, label, placeholder, required, maxLength, digitsOnly, onFieldBlur }: TextInputFieldProps) => {
  const { field, fieldState } = useController<UserFormValues>({ name });
  const { clearErrors } = useFormContext<UserFormValues>();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Errors are raised by Continue; entering something clears them again
    // rather than leaving a stale message while the user types.
    if (fieldState.error) clearErrors(name);
    const next = digitsOnly ? event.target.value.replace(/\D/g, '') : event.target.value;
    field.onChange(maxLength ? next.slice(0, maxLength) : next);
  };

  const handleBlur = () => {
    field.onBlur();
    onFieldBlur?.(field.value as string);
  };

  return (
    <FieldShell label={label} required={required} error={fieldState.error?.message}>
      <Input
        {...field}
        value={(field.value as string) ?? ''}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        maxLength={maxLength}
        inputMode={digitsOnly ? 'numeric' : undefined}
        className={clsx(styles.textInput, { [styles.hasError]: fieldState.error })}
      />
    </FieldShell>
  );
};

// A masked text input rather than type=password: browsers match on type, name
// and autocomplete to offer prefill, generation and saving, and an admin setting
// someone else's password should not populate their own profile. Falls back to a
// real password field where masking is unsupported.
const SUPPORTS_TEXT_SECURITY = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('-webkit-text-security', 'disc');

const blockEvent = (event: React.SyntheticEvent) => event.preventDefault();

export const PasswordInputField = ({ name, label, placeholder }: TextInputFieldProps) => {
  const { field, fieldState } = useController<UserFormValues>({ name });
  const { clearErrors } = useFormContext<UserFormValues>();
  const [isRevealed, setIsRevealed] = useState(false);

  return (
    <FieldShell label={label} error={fieldState.error?.message}>
      <Input
        {...field}
        // The name is one of the signals browsers match on, and react-hook-form
        // drives the value through its controller without it.
        name={undefined}
        type={SUPPORTS_TEXT_SECURITY ? 'text' : 'password'}
        value={(field.value as string) ?? ''}
        onChange={(event) => {
          if (fieldState.error) clearErrors(name);
          field.onChange(event);
        }}
        onCopy={blockEvent}
        onCut={blockEvent}
        onDragStart={blockEvent}
        placeholder={placeholder}
        // A password input suppresses these for free; a text input does not.
        autoComplete="off"
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
        aria-label={`${label} (hidden)`}
        data-lpignore="true"
        data-1p-ignore=""
        suffix={
          <button
            type="button"
            className={styles.revealButton}
            aria-label={isRevealed ? `Hide ${label}` : `Show ${label}`}
            onClick={() => setIsRevealed((revealed) => !revealed)}
          >
            {isRevealed ? <EyeOutlined /> : <EyeInvisibleOutlined />}
          </button>
        }
        className={clsx(styles.textInput, {
          [styles.maskedInput]: !isRevealed && SUPPORTS_TEXT_SECURITY,
          [styles.hasError]: fieldState.error,
        })}
      />
    </FieldShell>
  );
};

interface SelectInputFieldProps extends TextInputFieldProps {
  options: SelectProps['options'];
}

// The stepper body scrolls, so the menu has to render outside it.
const selectPopupContainer = () => document.body;

// A solid triangle, like the stepper's own footer carets -- antd's chevron is
// too light for this control.
const selectCaret = <span className={styles.selectCaret} />;

export const SelectInputField = ({ name, label, placeholder, options, required }: SelectInputFieldProps) => {
  const { field, fieldState } = useController<UserFormValues>({ name });
  const { clearErrors } = useFormContext<UserFormValues>();

  return (
    <FieldShell label={label} required={required} error={fieldState.error?.message} errorClassName={styles.selectErrorSlot}>
      <Select
        {...field}
        value={(field.value as string) ?? undefined}
        onChange={(value) => {
          if (fieldState.error) clearErrors(name);
          field.onChange(value);
        }}
        options={options}
        placeholder={placeholder}
        suffixIcon={selectCaret}
        getPopupContainer={selectPopupContainer}
        popupClassName={styles.selectPopup}
        className={clsx(styles.selectControl, { [styles.hasError]: fieldState.error })}
      />
    </FieldShell>
  );
};

interface ExpiryDatePickerProps {
  name: Path<UserFormValues>;
  disabled: boolean;
}

export const ExpiryDatePicker = ({ name, disabled }: ExpiryDatePickerProps) => {
  const { field, fieldState } = useController<UserFormValues>({ name });
  const { clearErrors } = useFormContext<UserFormValues>();
  const { subscriptionEndDate } = useUserFormMeta();

  // Access can't start in the past, and can't outlast the subscription that
  // pays for it. Without an end date the upper bound simply isn't applied.
  const isOutOfRange = (current: dayjs.Dayjs) =>
    current.isBefore(dayjs().startOf('day')) || (!!subscriptionEndDate && current.isAfter(dayjs(subscriptionEndDate).endOf('day')));

  return (
    <DatePicker
      value={field.value ? dayjs(field.value as string) : null}
      onChange={(date) => {
        if (fieldState.error) clearErrors(name);
        field.onChange(date ? date.toISOString() : null);
      }}
      disabled={disabled}
      disabledDate={isOutOfRange}
      allowClear={false}
      format="YYYY-MM-DD"
      placeholder="SELECT DATE"
      suffixIcon={null}
      className={styles.datePicker}
    />
  );
};
